// Admin organization + settings data layer.
//
// One row per org; admin can edit only their own org (RLS via
// organizations_admin_update policy, which requires
// id = current_organization_id()).

import { supabase, isSupabaseConfigured } from "../supabase";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

const FULL_COLUMNS = [
  "id",
  "name",
  "slug",
  "timezone",
  "display_name",
  "contact_email",
  "contact_phone",
  "contact_address",
  "logo_url",
  "brand_primary_hex",
  "brand_accent_hex",
  "monthly_tuition_default_cents",
  "late_fee_cents",
  "late_fee_grace_days",
  "sibling_discount_percent",
  "autopay_enabled",
  "feature_public_announcements",
  "feature_enrollment_open",
  "enforce_attendance_clearance",
  "created_at",
  "updated_at",
].join(", ");

export async function getOrganization(id) {
  ensureLive();
  const { data, error } = await supabase
    .from("organizations")
    .select(FULL_COLUMNS)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

/**
 * Applies a partial patch to the admin's own organization. Explicit
 * allow-list keeps the UI from accidentally writing to admin-restricted
 * columns (`id`, `slug`, `created_at`, `updated_at`).
 */
const ALLOWED_PATCH_KEYS = new Set([
  "name",
  "timezone",
  "display_name",
  "contact_email",
  "contact_phone",
  "contact_address",
  "logo_url",
  "brand_primary_hex",
  "brand_accent_hex",
  "monthly_tuition_default_cents",
  "late_fee_cents",
  "late_fee_grace_days",
  "sibling_discount_percent",
  "autopay_enabled",
  "feature_public_announcements",
  "feature_enrollment_open",
  "enforce_attendance_clearance",
]);

export async function updateOrganization(id, patch) {
  ensureLive();
  const clean = {};
  for (const [k, v] of Object.entries(patch || {})) {
    if (!ALLOWED_PATCH_KEYS.has(k)) continue;
    // Empty strings become nulls for optional text columns so we don't
    // store whitespace-only values.
    if (typeof v === "string" && v.trim() === "" && k !== "display_name" && k !== "name") {
      clean[k] = null;
    } else {
      clean[k] = v;
    }
  }
  const { data, error } = await supabase
    .from("organizations")
    .update(clean)
    .eq("id", id)
    .select(FULL_COLUMNS)
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    // 0 rows returned → RLS blocked it. Common cause: admin trying to
    // patch an org other than their own.
    throw new Error("Not authorised to update this organization.");
  }
  return data;
}

// ---------------------------------------------------------------------------
// Convenience helpers used by pages.
// ---------------------------------------------------------------------------

export const TIMEZONE_OPTIONS = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Europe/Madrid",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

export function dollarsToCents(str) {
  if (str == null || str === "") return null;
  const f = parseFloat(String(str).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(f)) return null;
  return Math.round(f * 100);
}

export function centsToDollars(cents) {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}

export function normalizeHex(value) {
  const v = String(value || "").trim();
  if (!v) return null;
  const withHash = v.startsWith("#") ? v : `#${v}`;
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(withHash) ? withHash.toUpperCase() : null;
}
